import React from "react";
import { CalendarClock, ListChecks, RefreshCw, ShieldCheck, AlertTriangle, HelpCircle, Info, Database } from "lucide-react";
import ConfidenceBadge from "./ConfidenceBadge";

const STATUS = {
  compliant: { label: "Compliant", cls: "bg-emerald-500/15 text-emerald-400", icon: ShieldCheck },
  action_needed: { label: "Action needed", cls: "bg-amber-500/15 text-amber-400", icon: AlertTriangle },
  unknown: { label: "Unknown", cls: "bg-white/10 text-white/60", icon: HelpCircle },
};

const FIELD_LABELS = {
  business_name: "Business Name", dba: "DBA", ein: "EIN", entity_type: "Entity Type",
  operating_states: "Operating States", registered_agent: "Registered Agent", owners: "Owners",
  business_address: "Business Address", mailing_address: "Mailing Address",
  insurance_policy_number: "Insurance Policy #", license_number: "License #",
  permit_number: "Permit #", tax_id: "Tax ID", issuing_agency: "Government Agency",
  renewal_date: "Renewal Date",
};

export default function AnalysisView({ analysis }) {
  if (!analysis) return null;
  const st = STATUS[analysis.compliance_status] || STATUS.unknown;
  const conf = Math.max(0, Math.min(100, analysis.confidence ?? 0));
  const fields = Object.entries(analysis.extracted_fields || {}).filter(
    ([, v]) => v && (!Array.isArray(v) || v.length)
  );

  return (
    <div className="space-y-6" data-testid="analysis-view">
      <div className="flex flex-wrap items-center gap-3">
        <ConfidenceBadge analysis={analysis} />
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${st.cls}`}>
          <st.icon size={13} /> {st.label}
        </span>
        <span className="rounded-full bg-dox-purple/15 text-dox-purple px-3 py-1 text-xs font-bold">
          {analysis.category}
        </span>
      </div>

      {/* confidence */}
      <div>
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-white/50 uppercase tracking-wider font-semibold">DOX confidence</span>
          <span className="font-mono font-bold text-white">{conf}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-dox-orange to-dox-magenta transition-all"
            style={{ width: `${conf}%` }}
          />
        </div>
        {analysis.confidence_reason && (
          <p className="text-xs text-white/45 mt-2">{analysis.confidence_reason}</p>
        )}
      </div>

      {fields.length > 0 && (
        <div className="rounded-xl border border-dox-purple/20 bg-dox-purple/[0.04] p-4">
          <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-wider font-semibold mb-3">
            <Database size={13} /> Extracted into Business Memory
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {fields.map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-white/45">{FIELD_LABELS[k] || k}</span>
                <span className="text-white/85 text-right font-medium">{Array.isArray(v) ? v.join(", ") : v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis.summary && (
        <Block title="Summary">
          <p className="text-white/70 text-sm leading-relaxed">{analysis.summary}</p>
        </Block>
      )}

      {analysis.plain_english && (
        <Block title="In plain English" accent>
          <p className="text-white/80 text-sm leading-relaxed">{analysis.plain_english}</p>
        </Block>
      )}

      {analysis.key_dates?.length > 0 && (
        <Block title="Key dates" icon={CalendarClock}>
          <ul className="space-y-2">
            {analysis.key_dates.map((d, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-white/60">{d.label}</span>
                <span className={`font-mono ${d.type === "deadline" ? "text-amber-400" : "text-white/80"}`}>{d.date}</span>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {analysis.action_items?.length > 0 && (
        <Block title="Action items" icon={ListChecks}>
          <ul className="space-y-2">
            {analysis.action_items.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-dox-orange shrink-0" />
                {a}
              </li>
            ))}
          </ul>
        </Block>
      )}

      {analysis.renewal_recommendation && analysis.renewal_recommendation !== "None" && (
        <Block title="Renewal recommendation" icon={RefreshCw}>
          <p className="text-white/70 text-sm leading-relaxed">{analysis.renewal_recommendation}</p>
        </Block>
      )}

      {analysis.sources?.length > 0 && (
        <div>
          <div className="text-xs text-white/50 uppercase tracking-wider font-semibold mb-2">Sources</div>
          <div className="flex flex-wrap gap-2">
            {analysis.sources.map((s, i) => (
              <span key={i} className="rounded-lg bg-white/5 border border-white/10 px-2.5 py-1 text-xs text-white/60">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-xl bg-white/[0.03] border border-white/10 p-3">
        <Info size={14} className="text-white/40 mt-0.5 shrink-0" />
        <p className="text-xs text-white/45 leading-relaxed">
          {analysis.disclaimer || "This is not legal advice."}
          {analysis.model_used && <span className="block mt-1 font-mono text-white/30">Analyzed by {analysis.model_used}</span>}
        </p>
      </div>
    </div>
  );
}

function Block({ title, children, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-dox-orange/20 bg-dox-orange/[0.04]" : "border-white/10 bg-surface"}`}>
      <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-wider font-semibold mb-2">
        {Icon && <Icon size={13} />} {title}
      </div>
      {children}
    </div>
  );
}
